import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMembershipStatus, MembershipStatus } from '../services/invenioApi';
import { getMembershipBanner } from '../utils/membershipExpiry';

/**
 * Site-wide renewal bar for the last week of a membership and beyond.
 *
 * Amber through the final week, red from the expiry date, and still red once
 * DOI generation has been paused. It is not dismissible: it states a live
 * status that only renewal can clear, so hiding it would hide the reason a
 * member's uploads are about to start failing.
 *
 * Renders nothing for signed-out visitors and for accounts with no membership,
 * so the marketing site and individual researchers are unaffected.
 */
const MembershipRenewalBar: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [status, setStatus] = useState<MembershipStatus | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await getMembershipStatus());
    } catch {
      // A bar that cannot load must not break the page it sits above. The
      // halt is enforced server-side regardless of what this bar shows.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setStatus(null);
      return;
    }
    void load();
    // Re-resolved per signed-in account, so switching users cannot leave the
    // previous member's expiry on screen.
  }, [isAuthenticated, isLoading, user?.id, load]);

  const banner = getMembershipBanner(status);
  if (!banner) return null;

  const Icon = banner.halted ? AlertCircle : AlertTriangle;
  // The renewal checkout is a route in this app, so it must not open in a new
  // tab. An operator can still point the call to action at an external URL.
  const isInternal = banner.actionUrl.startsWith('/');
  const actionClassName = `shrink-0 rounded-md px-3 py-1.5 text-sm font-semibold transition ${
    banner.tone === 'critical'
      ? 'bg-white text-red-700 hover:bg-red-50'
      : 'bg-amber-600 text-white hover:bg-amber-700'
  }`;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full border-b px-4 py-3 ${banner.className}`}
      data-testid="membership-renewal-bar"
      data-tone={banner.tone}
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${banner.iconClassName}`} aria-hidden="true" />
          <p className="text-sm font-medium">{banner.message}</p>
        </div>
        {isInternal ? (
          <Link to={banner.actionUrl} className={actionClassName}>
            {banner.actionLabel}
          </Link>
        ) : (
          <a
            href={banner.actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={actionClassName}
          >
            {banner.actionLabel}
          </a>
        )}
      </div>
    </div>
  );
};

export default MembershipRenewalBar;
